import {
  Text,
  FlatActionList,
  Stack,
  Divider,
  Label,
  TextField,
  Button,
  Tooltip,
} from "@towersystems/roam-common-native";
import { textButtonProps } from "../../constants";
import { View } from "react-native-ui-lib";
import { Controller, useFormContext } from "react-hook-form";
import { ProductFormValues } from "../../types";
import React from "react";
import { AntDesign } from "@expo/vector-icons";
import { MarkupStyle } from "@towersystems/roam-common/lib/generated-types";
import { TaxGroupIdToName } from "../../../../../../components/TaxGroupIdToName";
import { useTranslation } from "../../../../../../utilities/i18n";
import { LllmQuickWinButton } from "../../../../../../components/LlmQuickWinButton";
import { useLlmQuickWinsProvider } from "../../../../../../utilities/llm-quick-wins";

export interface PricingFieldsetProps {
  onSelectTaxGroupPress?: () => void;
  onRetailPricingLlmQuickWinButtonPress?: () => void;
}

export const PricingFieldset = ({
  onSelectTaxGroupPress,
  onRetailPricingLlmQuickWinButtonPress,
}: PricingFieldsetProps) => {
  const { t } = useTranslation("product");

  const { control, watch, getValues, setValue, formState, resetField } =
    useFormContext<ProductFormValues>();
  const { strategyEnabled } = useLlmQuickWinsProvider();

  const { errors, touchedFields, isDirty } = formState;
  const watchedTaxGroupId = watch("taxGroupId");
  const watchedTaxPercent = watch("taxRatePercent");
  const watchedMarkupPercent = watch("markupPercent");
  const watchedTradeMarkupPercent = watch("tradeMarkupPercent");
  const watchedTradeMarkupType = watch("tradeMarkupType");
  const watchedCostPrice = watch("costPrice");
  const watchedBarcode = watch("stockNumber");
  const nameValue = watch("name");

  const watchedGlobalDisable = watch("globalDisable");

  React.useEffect(() => {
    if (isDirty) {
      recalculateRetailPrice();
    }
  }, [watchedTradeMarkupPercent]);

  React.useEffect(() => {
    if (isDirty) {
      calculateCostPrices(getValues("costPrice"), true);
    }
  }, [watchedTaxPercent]);

  const calculateCostPrices = React.useCallback(
    (costPrice: string, incGst: boolean = true) => {
      const taxRate = getValues("taxRatePercent");

      if (incGst) {
        const taxAmount =
          taxRate > 0
            ? parseFloat(costPrice) / (1 + taxRate / 100)
            : parseFloat(costPrice);

        if (isNaN(taxAmount)) {
          return;
        }

        setValue("costPriceExlTax", taxAmount.toFixed(2).toString(), {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: false,
        });
        return;
      } else {
        const taxAmount =
          taxRate > 0 ? parseFloat(costPrice) * (taxRate / 100) : 0;

        const priceIncGst = parseFloat(costPrice) + taxAmount;

        if (isNaN(priceIncGst)) {
          return;
        }

        setValue("costPrice", priceIncGst.toFixed(2).toString(), {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: false,
        });

        return;
      }
    },
    [setValue, getValues]
  );

  const recalculateRetailPrice = React.useCallback(() => {
    const costPrice = getValues("costPrice");
    const markupPercent = getValues("markupPercent");

    const retailPrice =
      markupPercent > 0
        ? parseFloat(costPrice) * (1 + markupPercent / 100)
        : parseFloat(costPrice);

    if (isNaN(retailPrice)) {
      return;
    }

    setValue("retailPrice", retailPrice.toFixed(2).toString(), {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: false,
    });
  }, [getValues]);

  const calculateFromRetailPrice = React.useCallback(
    (force: boolean = true) => {
      const retailPrice = getValues("retailPrice");
      const markupPercent = getValues("markupPercent");

      const costPrice =
        markupPercent > 0
          ? parseFloat(retailPrice) / (1 + markupPercent / 100)
          : parseFloat(retailPrice);

      if (isNaN(costPrice)) {
        return;
      }

      const touched = touchedFields.costPrice || touchedFields.costPriceExlTax;

      if (force || !touched) {
        resetField("costPriceExlTax", {
          defaultValue: costPrice.toFixed(2).toString(),
          keepDirty: true,
          keepTouched: false,
          keepError: true,
        });
        resetField("costPrice", {
          defaultValue: costPrice.toFixed(2).toString(),
          keepDirty: true,
          keepTouched: false,
          keepError: true,
        });
        calculateCostPrices(costPrice.toFixed(2).toString(), true);
      }
    },
    [getValues, resetField, touchedFields]
  );

  const calculateStockTradePrice = React.useCallback(() => {
    const tradeMarkupPercent = getValues("tradeMarkupPercent");
    const retailPrice = parseFloat(getValues("retailPrice"));
    const costPrice = parseFloat(getValues("costPrice"));

    let tradePrice = 0;

    if (tradeMarkupPercent > 0) {
      switch (getValues("tradeMarkupType")) {
        case MarkupStyle.ABOVE_COST:
          tradePrice = costPrice + costPrice * (tradeMarkupPercent / 100);

          break;
        case MarkupStyle.OFF_RETAIL:
          tradePrice = retailPrice - retailPrice * (tradeMarkupPercent / 100);
          break;
        default:
          tradePrice = retailPrice;
      }
    } else {
      tradePrice = retailPrice;
    }

    if (isNaN(tradePrice)) {
      return;
    }

    setValue("tradePrice", tradePrice.toFixed(2).toString(), {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: false,
    });
  }, [getValues, setValue]);

  const commonProps = {
    disabled: watchedGlobalDisable,
  };

  const textProps = {
    ...textButtonProps,
    ...(watchedGlobalDisable ? { color: "subdued" } : {}),
  };

  return (
    <Stack vertical>
      <FlatActionList
        items={[
          {
            ...commonProps,
            key: "taxGroupId",
            title: t("form.tax_group.label"),
            subTitle: t("form.tax_group.description"),
            onPress: onSelectTaxGroupPress,
            componentRight: () =>
              watchedTaxGroupId ? (
                <TaxGroupIdToName
                  taxGroupId={watchedTaxGroupId}
                  {...textProps}
                />
              ) : (
                <Text {...textProps}>{t("form.tax_group.placeholder")}</Text>
              ),
          },
        ]}
      />
      <Divider marginVertical={0} />
      <View>
        <View>
          <Label required label={t("form.cost_price.label")}></Label>
          <Controller
            name="costPrice"
            control={control}
            {...commonProps}
            render={({
              field: { onChange, value, onBlur, disabled },
              fieldState: { error },
            }) => (
              <TextField
                onBlur={onBlur}
                value={value ?? ""}
                readonly={disabled}
                onChangeText={(v) => {
                  onChange(v);
                  !error && calculateCostPrices(v, true);
                  !error && recalculateRetailPrice();
                }}
                keyboardType="numeric"
                validationMessage={error ? error.message : undefined}
                placeholder={t("form.cost_price.placeholder")}
                formatterType={errors.costPrice?.message ? undefined : "money"}
              />
            )}
          />
        </View>
      </View>

      <View>
        <View>
          <Label required label={t("form.cost_price_ex_gst.label")}></Label>
          <Controller
            name="costPriceExlTax"
            control={control}
            {...commonProps}
            render={({
              field: { onChange, value, onBlur, disabled },
              fieldState: { error },
            }) => (
              <TextField
                onBlur={onBlur}
                value={value ?? ""}
                readonly={disabled}
                onChangeText={(v) => {
                  onChange(v);
                  !error && calculateCostPrices(v, false);
                  !error && recalculateRetailPrice();
                }}
                keyboardType="numeric"
                validationMessage={error ? error.message : undefined}
                placeholder={t("form.cost_price_ex_gst.placeholder")}
                formatterType={
                  errors.costPriceExlTax?.message ? undefined : "money"
                }
              />
            )}
          />
        </View>
      </View>
      <View>
        <View>
          <Label required label={t("form.retail_price.label")}>
            {watchedMarkupPercent ? (
              <Text>
                {t("watched_markup_percent", {
                  percent: watchedMarkupPercent,
                })}
              </Text>
            ) : undefined}
            {onRetailPricingLlmQuickWinButtonPress &&
            strategyEnabled("product_price_sugestions_quick_win") ? (
              <View style={{ marginLeft: 8 }}>
                <LllmQuickWinButton
                  onPress={onRetailPricingLlmQuickWinButtonPress}
                  disabled={!watchedCostPrice || !nameValue || !watchedBarcode}
                  hoverText="Generate Retail Price with AI"
                />
              </View>
            ) : null}
          </Label>
          <Controller
            name="retailPrice"
            control={control}
            {...commonProps}
            render={({
              field: { onChange, value, onBlur, disabled },
              fieldState: { error },
            }) => (
              <TextField
                onBlur={onBlur}
                value={value ?? ""}
                readonly={disabled}
                onChangeText={(value) => {
                  onChange(value);
                  !error && calculateFromRetailPrice(false);
                }}
                keyboardType="numeric"
                validationMessage={error ? error.message : undefined}
                placeholder={t("form.retail_price.placeholder")}
                formatterType={
                  errors.retailPrice?.message ? undefined : "money"
                }
                trailingAccessory={
                  <Tooltip text="Calculate prices from Retail Price">
                    <Button
                      size="compact"
                      {...commonProps}
                      onPress={() => calculateFromRetailPrice(true)}
                    >
                      <Text color="action">
                        <AntDesign name="calculator" size={24} />
                      </Text>
                    </Button>
                  </Tooltip>
                }
              />
            )}
          />
        </View>
      </View>
      <View>
        <View>
          <Label label={t("form.trade_price.label")}>
            {watchedTradeMarkupPercent ? (
              <Text>{` (Inc ${watchedTradeMarkupPercent}% Markup, ${watchedTradeMarkupType})`}</Text>
            ) : undefined}
          </Label>
          <Controller
            name="tradePrice"
            control={control}
            {...commonProps}
            render={({
              field: { onChange, value, disabled },
              fieldState: { error },
            }) => (
              <TextField
                value={value ?? ""}
                onChangeText={onChange}
                keyboardType="numeric"
                readonly={disabled}
                validationMessage={error ? error.message : undefined}
                placeholder={t("form.trade_price.placeholder")}
                formatterType={errors.tradePrice?.message ? undefined : "money"}
              />
            )}
          />
        </View>
      </View>
    </Stack>
  );
};
